import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import SharedHeader from './SharedHeader';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Calendar,
  Tag,
  Percent,
  Clock,
  Eye,
  Users,
  TrendingUp,
  X,
  ArrowLeft
} from 'lucide-react';
import { getProfileUpdate, PutBusinessProfile, AllProfileUpdate } from '../../services/api';
import { toast } from 'react-hot-toast';

const ManageOffers = ({onBack}) => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileDocId, setProfileDocId] = useState(null);
  const [profileShops, setProfileShops] = useState([]); // full shopDetails structure
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOfferDetails, setShowOfferDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
  const [createOfferForm, setCreateOfferForm] = useState({
    title: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    startDate: '',
    endDate: '',
    usageLimit: '',
    code: ''
  });
  const [editOfferForm, setEditOfferForm] = useState({});
  // Date helpers for min attributes (prevent past dates)
  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchOffers();
  }, [currentUser]);

  const fetchOffers = async () => {
    if (!currentUser) return;
  
    try {
      setLoading(true);
  
      // Load profiles filtered by current user's profile_id
      const resp = await getProfileUpdate({ profile_id: currentUser?.uid });
      const data = Array.isArray(resp.extractedData) 
        ? resp.extractedData 
        : (resp.extractedData?.records || []);
  
      // Find the profile that matches currentUser.uid
      const userProfile = Array.isArray(data) 
        ? data.find(profile => profile._id === currentUser.uid || profile.profile_id === currentUser.uid)
        : data; // If it's a single object, just use it
  
      if (!userProfile) {
        toast.error("No profile found for current user");
        setOffers([]);
        return;
      }
  
      // Save the profile docId for future updates
      setProfileDocId(userProfile._id || userProfile.profile_id || null);
  
      // Extract shop details
      const shopsArr = Array.isArray(userProfile.shopDetails) ? userProfile.shopDetails : [];
      setProfileShops(shopsArr);
  
      // Prepare all offers
      if (shopsArr.length > 0) {
        const allOffers = shopsArr.flatMap((shop, sIdx) => {
          const offs = Array.isArray(shop.offers) ? shop.offers : [];
          return offs.map((offer, oIdx) => ({
            id: offer._id || offer.id || `${shop.shopName || 'shop'}_${oIdx}`,
            offerDocId: offer._id || null,
            shopIndex: sIdx,
            offerIndex: oIdx,
            title: offer.title || 'Untitled Offer',
            code: offer.code || 'NO_CODE',
            subtitle: offer.subtitle || 'No description',
            expiry: offer.expiry || offer.expity || 'No expiry',
            discount: extractDiscountFromTitle(offer.title || ''),
            status: getOfferStatus(offer.expiry || offer.expity),
            views: Math.floor(Math.random() * 500) + 50,
            used: Math.floor(Math.random() * 100) + 10,
            createdDate: offer.createdDate ? new Date(offer.createdDate) : new Date(),
            category: shop.category || 'General',
            shopName: shop.shopName || 'Unnamed Shop',
          }));
        });
        setOffers(allOffers);
      } else {
        setOffers([]);
        toast('No offers found');
      }
    } catch (error) {
      console.error("Error fetching offers:", error);
      toast.error("Failed to load offers");
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };
  
  
  const businessConfig = {
    businessName: currentUser?.displayName || 'My Business',
    businessEmail: currentUser?.email || 'business@example.com',
    businessPhone: '+91 98765 43210',
    businessAddress: '123 Business Street, City, State 12345',
    fixedDiscount: 10,
    pointsPerRupee: 0.1,
    minRedemptionPoints: 100
  };  

  const extractDiscountFromTitle = (title) => {
    const match = title?.match(/(\d+)%/);
    return match ? `${match[1]}%` : 'N/A';
  };

  const getOfferStatus = (expiry) => {
    if (!expiry || expiry === 'No expiry') return 'active';
    
    // Simple logic to determine status based on expiry text
    if (expiry.includes('day')) {
      const days = parseInt(expiry.match(/\d+/)?.[0] || 0);
      return days > 0 ? 'active' : 'expired';
    }
    
    return 'active';
  };

  const handleOfferClick = (offer) => {
    setSelectedOffer(offer);
    setShowOfferDetails(true);
  };

  const handleEditOffer = (offer, e) => {
    e.stopPropagation();
    setSelectedOffer(offer);
    setEditOfferForm({
      title: offer.title,
      subtitle: offer.subtitle,
      code: offer.code,
      discount: offer.discount,
      expiry: offer.expiry,
      status: offer.status
    });
    setShowEditModal(true);
  };

  const handleDeleteOffer = (offer, e) => {
    e.stopPropagation();
    setSelectedOffer(offer);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    handleDeleteOfferAPI();
  };

  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         offer.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || offer.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      expired: { bg: 'bg-red-100', text: 'text-red-800', label: 'Expired' },
      paused: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Paused' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading offers...</p>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/business/dashboard');
  };

  const handleMenuAction = (action) => {
    switch (action) {
      case 'sales-history':
        navigate('/business/sales');
        break;
      case 'dashboard':
        navigate('/business/dashboard');
        break;
      default:
        break;
    }
  };

  // Helper function to generate offer code
  const generateOfferCode = (title) => {
    const words = title.split(' ');
    const code = words.map(word => word.substring(0, 2).toUpperCase()).join('');
    return code + Math.floor(Math.random() * 1000);
  };

 // Create offer function
const handleCreateOffer = async () => {
  try {
    if (!createOfferForm.title || !createOfferForm.discountValue) {
      toast.error('Please fill in all required fields');
      return;
    }

    const uid = sessionStorage.getItem('uid') || currentUser?.uid;
    if (!uid) {
      toast.error('User ID not found');
      return;
    }

    // Generate offer code if not provided
    const offerCode = createOfferForm.code || generateOfferCode(createOfferForm.title);

    if (!profileDocId) {
      // Try to resolve docId using profile_id
      try {
        const lookup = await getProfileUpdate({ profile_id: uid });
        const list = Array.isArray(lookup.extractedData) ? lookup.extractedData : (lookup.extractedData?.records || []);
        const found = Array.isArray(list) ? list.find(p => p.profile_id === uid) : null;
        if (found?._id || found?.id) {
          setProfileDocId(found._id || found.id);
        }
      } catch (_) {}
    }

    // Build new offer (omit _id so backend creates)
    const newOfferObj = {
      title: createOfferForm.title,
      subtitle: createOfferForm.description,
      code: offerCode,
      discountType: createOfferForm.discountType,
      discountValue: createOfferForm.discountValue,
      startDate: createOfferForm.startDate,
      endDate: createOfferForm.endDate,
      // Expiry fields: store both for compatibility with existing records
      expiry: createOfferForm.endDate || 'No expiry',
      expity: createOfferForm.endDate || 'No expiry',
      usageLimit: createOfferForm.usageLimit || null,
      status: 'active',
      createdDate: new Date().toISOString(),
      views: 0,
      used: 0,
    };

    // Push into first shop (or enhance to selected shop)
    const shopsClone = JSON.parse(JSON.stringify(profileShops));
    const targetShopIndex = 0;
    shopsClone[targetShopIndex] = shopsClone[targetShopIndex] || {};
    shopsClone[targetShopIndex].offers = Array.isArray(shopsClone[targetShopIndex].offers)
      ? shopsClone[targetShopIndex].offers
      : [];
    shopsClone[targetShopIndex].offers.push(newOfferObj);

    // If we have a document id, PUT; else create profile via POST first
    if (profileDocId) {
      if (profileDocId) {
        await PutBusinessProfile(profileDocId, { shopDetails: shopsClone });
      } else {
        await AllProfileUpdate({ profile_id: uid, shopDetails: shopsClone });
        const ref = await getProfileUpdate({ profile_id: uid });
        const arr = Array.isArray(ref.extractedData) ? ref.extractedData : (ref.extractedData?.records || []);
        const prof = Array.isArray(arr) ? arr.find(p => p.profile_id === uid) : null;
        if (prof?._id || prof?.id) setProfileDocId(prof._id || prof.id);
      }
    } else {
      // If there are no shops yet, ensure we send a minimal shop structure
      const payloadShops = shopsClone.length ? shopsClone : [{ shopName: businessConfig.businessName, category: 'General', offers: [newOfferObj] }];
      await AllProfileUpdate({ profile_id: uid, shopDetails: payloadShops });
      // Refetch to capture new docId
      const ref = await getProfileUpdate({ profile_id: uid });
      const arr = Array.isArray(ref.extractedData) ? ref.extractedData : (ref.extractedData?.records || []);
      const prof = Array.isArray(arr) ? arr.find(p => p.profile_id === uid) : null;
      if (prof?._id || prof?.id) setProfileDocId(prof._id || prof.id);
    }

    toast.success('Offer created successfully!');
    setProfileShops(shopsClone);
    // Reset form and refresh
    setCreateOfferForm({
      title: '',
      description: '',
      discountType: 'percentage',
      discountValue: '',
      startDate: '',
      endDate: '',
      usageLimit: '',
      code: ''
    });
    setShowCreateOfferModal(false);
    await fetchOffers();
  } catch (error) {
    console.error('Error creating offer:', error);
    toast.error('Failed to create offer. Please try again.');
  }
};

  // Edit offer function
  const handleUpdateOffer = async () => {
    try {
      if (!editOfferForm.title || !editOfferForm.discount) {
        toast.error('Please fill in all required fields');
        return;
      }

      const uid = sessionStorage.getItem('uid') || currentUser?.uid;
      if (!uid) {
        toast.error('User ID not found');
        return;
      }

      if (!profileDocId) {
        // Try resolve docId using profile_id
        try {
          const lookup = await getProfileUpdate({ profile_id: uid });
          const list = Array.isArray(lookup.extractedData) ? lookup.extractedData : (lookup.extractedData?.records || []);
          const found = Array.isArray(list) ? list.find(p => p.profile_id === uid) : null;
          if (found?._id || found?.id) {
            setProfileDocId(found._id || found.id);
          }
        } catch (_) {}
      }
      const shopsClone = JSON.parse(JSON.stringify(profileShops));
      const { shopIndex, offerIndex } = selectedOffer;
      if (shopsClone?.[shopIndex]?.offers?.[offerIndex]) {
        const target = shopsClone[shopIndex].offers[offerIndex];
        target.title = editOfferForm.title;
        target.subtitle = editOfferForm.subtitle;
        target.code = editOfferForm.code;
        target.discount = editOfferForm.discount; // keep compatibility
        // Set both expiry and legacy expity
        target.expiry = editOfferForm.expiry;
        target.expity = editOfferForm.expiry;
        target.status = editOfferForm.status || target.status;
      }
      await PutBusinessProfile(profileDocId, { shopDetails: shopsClone });
      toast.success('Offer updated successfully!');
      setProfileShops(shopsClone);
      setShowEditModal(false);
      setSelectedOffer(null);
      setEditOfferForm({});
      await fetchOffers();
    } catch (error) {
      console.error('Error updating offer:', error);
      toast.error('Failed to update offer. Please try again.');
    }
  };

  // Delete offer function
  const handleDeleteOfferAPI = async () => {
    try {
      if (!profileDocId) {
        toast.error('Profile not loaded');
        return;
      }
      const shopsClone = JSON.parse(JSON.stringify(profileShops));
      const { shopIndex, offerIndex } = selectedOffer || {};
      if (typeof shopIndex === 'number' && typeof offerIndex === 'number' && shopsClone?.[shopIndex]?.offers) {
        shopsClone[shopIndex].offers.splice(offerIndex, 1);
      }
      await PutBusinessProfile(profileDocId, { shopDetails: shopsClone });
      toast.success('Offer deleted successfully!');
      setProfileShops(shopsClone);
      setShowDeleteModal(false);
      setSelectedOffer(null);
      await fetchOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Failed to delete offer. Please try again.');
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <SharedHeader 
              title={businessConfig.businessName}
              subtitle="Business Dashboard"
              onMenuAction={handleMenuAction}
            />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create Offer Button */}
        <div className="flex items-center gap-4 mb-6">
          <button
             onClick={onBack}
            className="text-indigo-600 hover:underline font-medium"
          >
            ← Back
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Manage Offers</h2>
        </div>
        <div className="flex justify-end mb-6">
          <button 
            onClick={() => setShowCreateOfferModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create Offer</span>
          </button>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search offers by title or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>
        </div>

        {/* Offers Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Your Offers</h2>
          </div>
          
          {sortedOffers.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                    <th className="text-left py-3 px-6 font-semibold text-gray-700">Shop Name</th>
                      <th 
                        className="text-left py-3 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('title')}
                      >
                        Offer Details {sortBy === 'title' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th 
                        className="text-left py-3 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('code')}
                      >
                        Code {sortBy === 'code' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-700">Discount</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-700">Validity</th>
                      <th 
                        className="text-right py-3 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('views')}
                      >
                        Views {sortBy === 'views' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th 
                        className="text-right py-3 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('used')}
                      >
                        Used {sortBy === 'used' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedOffers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((offer) => (
                    <tr 
                      key={offer.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleOfferClick(offer)}
                    >
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-semibold text-gray-900">{offer.shopName}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-semibold text-gray-900">{offer.title}</p>
                          <p className="text-sm text-gray-500">{offer.subtitle}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded font-mono text-sm">
                          {offer.code}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                          {offer.discount}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">{offer.expiry}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <Eye className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{offer.views.toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{offer.used}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {getStatusBadge(offer.status)}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={(e) => handleEditOffer(offer, e)}
                            className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                            title="Edit Offer"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={(e) => handleDeleteOffer(offer, e)}
                            className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                            title="Delete Offer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-between border-t px-6 py-4">
              <p className="text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedOffers.length)} of {sortedOffers.length} offers
              </p>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                {[...Array(Math.ceil(sortedOffers.length / itemsPerPage))].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === i + 1 
                        ? 'bg-blue-600 text-white' 
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(sortedOffers.length / itemsPerPage)))}
                  disabled={currentPage === Math.ceil(sortedOffers.length / itemsPerPage)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
          ) : (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No offers found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Create your first offer to start attracting customers'
                }
              </p>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors">
                <Plus className="w-4 h-4" />
                <span>Create Your First Offer</span>
              </button>
            </div>
          )}
        </div>

        {/* Offer Details Modal (ATM Receipt Style) */}
        {showOfferDetails && selectedOffer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 text-center">
                <h2 className="text-xl font-bold">OFFER RECEIPT</h2>
                <p className="text-blue-100 text-sm mt-1">{selectedOffer.shopName}</p>
                <div className="mt-4 text-center">
                  <div className="text-3xl font-bold">{selectedOffer.discount}</div>
                  <div className="text-blue-100">DISCOUNT</div>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="font-bold text-lg text-gray-900">{selectedOffer.title}</h3>
                  <p className="text-gray-600 text-sm">{selectedOffer.subtitle}</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Offer Code:</span>
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">{selectedOffer.code}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Category:</span>
                    <span className="font-medium">{selectedOffer.category}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Validity:</span>
                    <span className="font-medium">{selectedOffer.expiry}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    {getStatusBadge(selectedOffer.status)}
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Analytics</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{selectedOffer.views}</div>
                      <div className="text-xs text-blue-600">Views</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{selectedOffer.used}</div>
                      <div className="text-xs text-green-600">Used</div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Purchase Amount:</span>
                    <span className="font-medium">₹{(selectedOffer.views * 150).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mt-1">
                    <span>Conversion Rate:</span>
                    <span className="font-medium">{((selectedOffer.used / selectedOffer.views) * 100).toFixed(1)}%</span>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4 text-center text-xs text-gray-500">
                  <p>Generated on {new Date().toLocaleDateString()}</p>
                  <p>Thank you for using our platform!</p>
                </div>
              </div>
              
              <div className="flex gap-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => handleEditOffer(selectedOffer, { stopPropagation: () => {} })}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => setShowOfferDetails(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && selectedOffer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Edit Offer</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Offer Title</label>
                    <input
                      type="text"
                      value={editOfferForm.title || ''}
                      onChange={(e) => setEditOfferForm({...editOfferForm, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={editOfferForm.subtitle || ''}
                      onChange={(e) => setEditOfferForm({...editOfferForm, subtitle: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Offer Code</label>
                      <input
                        type="text"
                        value={editOfferForm.code || ''}
                        onChange={(e) => setEditOfferForm({...editOfferForm, code: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Discount</label>
                      <input
                        type="text"
                        value={editOfferForm.discount || ''}
                        onChange={(e) => setEditOfferForm({...editOfferForm, discount: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Validity</label>
                    <input
                      type="text"
                      value={editOfferForm.expiry || ''}
                      onChange={(e) => setEditOfferForm({...editOfferForm, expiry: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-200">
                <button
                  onClick={handleUpdateOffer}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedOffer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete Offer</h3>
                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete "{selectedOffer.title}"? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={confirmDelete}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Offer Modal */}
        {showCreateOfferModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Create New Offer</h2>
                <button
                  onClick={() => setShowCreateOfferModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6">
                <form className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Offer Title
                    </label>
                    <input
                      type="text"
                      value={createOfferForm.title}
                      onChange={(e) => setCreateOfferForm({...createOfferForm, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter offer title"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={createOfferForm.description}
                      onChange={(e) => setCreateOfferForm({...createOfferForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter offer description"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Type
                      </label>
                      <select 
                        value={createOfferForm.discountType}
                        onChange={(e) => setCreateOfferForm({...createOfferForm, discountType: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Value
                      </label>
                      <input
                        type="number"
                        value={createOfferForm.discountValue}
                        onChange={(e) => setCreateOfferForm({...createOfferForm, discountValue: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter discount value"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={createOfferForm.startDate}
                        onChange={(e) => setCreateOfferForm({...createOfferForm, startDate: e.target.value})}
                        min={todayStr}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={createOfferForm.endDate}
                        onChange={(e) => setCreateOfferForm({...createOfferForm, endDate: e.target.value})}
                        min={createOfferForm.startDate || todayStr}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Usage Limit (Optional)
                    </label>
                    <input
                      type="number"
                      value={createOfferForm.usageLimit}
                      onChange={(e) => setCreateOfferForm({...createOfferForm, usageLimit: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter usage limit"
                    />
                  </div>
                </form>
              </div>
              
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowCreateOfferModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateOffer}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Create Offer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageOffers;
